<template>
    <v-form ref="form" v-model="isValid">
        <selector-password-mode
            ref="password-mode"
            v-model="passwordModeData"
            :is-ssh-key-selectable="isSshKeySelectable"
            :is-used-ssh-key="isUsedSshKey"
            :disabled="isDisabled || !isEditable || isRandomizeTypeComprehensive"
            class="text-caption"
        />
        <text-field-label
            v-if="isDisplayPassword"
            ref="password"
            v-model="passwordData"
            :rules="
                isRequired || !isPasswordExist
                    ? [rules.required, rules.maxChars(passwordData, 256)]
                    : [rules.maxChars(passwordData, 256)]
            "
            :disabled="isDisabled"
            :type="isShowPassword ? 'text' : 'password'"
            :append-icon="isShowPassword ? 'visibility_off' : 'visibility'"
            :required="isRequired"
            :hint="passwordData === '' && isPasswordExist ? $t('Password_has_been_saved') : ''"
            :label="$t('Password')"
            persistent-hint
            @click:append="isShowPassword = !isShowPassword"
        />
        <text-area-label
            v-if="isDisplayKey"
            ref="secret-key"
            v-model="sshKeyData"
            :rules="
                isRequired || !isKeyExist
                    ? [rules.required, rules.maxChars(sshKeyData, 2048)]
                    : [rules.maxChars(sshKeyData, 2048)]
            "
            :disabled="isDisabled"
            :required="isRequired"
            :hint="sshKeyData === '' && isKeyExist ? $t('Private_key_has_been_saved') : ''"
            :label="$t('Private_Key')"
            :append-icon="editIcon"
            :rows="2"
            class="pt-3 pb-2"
            persistent-hint
        />
        <text-field-label
            v-if="isDisplayKey"
            ref="secret-key-passphrase"
            v-model="sshKeyPassphraseData"
            :rules="[rules.maxChars(sshKeyPassphraseData, 256)]"
            :disabled="isDisabled"
            :type="isShowPassphrase ? 'text' : 'password'"
            :append-icon="isShowPassphrase ? 'visibility_off' : 'visibility'"
            :hint="sshKeyPassphraseData === '' && isPassphraseExist ? $t('Passphrase_has_been_saved') : ''"
            :label="$t('Private_Key_Passphrase')"
            class="py-2"
            persistent-hint
            @click:append="isShowPassphrase = !isShowPassphrase"
        />
    </v-form>
</template>

<script lang="ts">
import { mixins } from 'vue-class-component';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { SelectorPasswordMode, TextAreaLabel, TextFieldLabel } from '../../atoms';
import InputValidation from '../../../mixins/InputValidation';

@Component({
    name: 'PasswordModeInputter',
    components: {
        SelectorPasswordMode,
        TextFieldLabel,
        TextAreaLabel,
    },
})
export default class PasswordModeInputter extends mixins(InputValidation) {
    @Prop({ default: undefined }) readonly passwordMode: 'password' | 'ssh_key' | undefined;
    @Prop({ default: undefined }) readonly password: string | undefined;
    @Prop({ default: undefined }) readonly sshKey: string | undefined;
    @Prop({ default: undefined }) readonly sshKeyPassphrase: string | undefined;
    @Prop({ default: false }) disabled: boolean;
    @Prop({ default: false }) required: boolean;
    @Prop({ default: true }) editable: boolean;
    @Prop({ default: false }) isPasswordExist: boolean;
    @Prop({ default: false }) isKeyExist: boolean;
    @Prop({ default: false }) isPassphraseExist: boolean;
    @Prop({ default: false }) isEditAnyAccount: boolean;
    @Prop({ default: false }) readonly isRandomizeTypeComprehensive: boolean;
    @Prop({ default: false }) readonly isSshKeySelectable: boolean;
    @Prop({ default: false }) readonly isUsedSshKey: boolean;

    @Watch('isDisplayPassword')
    onIsFixChanged(val: boolean): void {
        if (val) {
            // パスワード入力欄が現れたらチェックを掛ける
            this.$nextTick((this as any).$refs.form.validate);
        }
    }
    @Watch('isDisplayKey')
    onIsKeyChanged(val: boolean): void {
        if (!val) {
            this.sshKeyData = '';
            this.sshKeyPassphraseData = '';
        } else {
            // パスワード入力欄が現れたらチェックを掛ける
            this.$nextTick((this as any).$refs.form.validate);
        }
    }
    @Watch('isValid', { immediate: true })
    onIsValidChanged(isValid: boolean): void {
        this.$emit('change-valid', isValid);
    }

    isShowPassword = false;
    isShowPassphrase = false;

    /**
     * getter, setter
     */
    get isRequired(): boolean {
        return this.required;
    }
    get isDisabled(): boolean {
        return this.disabled;
    }
    get isPassword(): boolean {
        return this.passwordModeData === 'password';
    }
    get isKey(): boolean {
        return this.passwordModeData === 'ssh_key';
    }
    get isEditable(): boolean {
        return this.editable;
    }
    get editIcon(): string {
        return this.isEditAnyAccount ? 'edit' : '';
    }
    get passwordModeData(): 'password' | 'ssh_key' | undefined {
        return this.passwordMode;
    }
    set passwordModeData(newVal: 'password' | 'ssh_key' | undefined) {
        this.$emit('update:passwordMode', newVal);
    }
    get passwordData(): string | undefined {
        return this.password;
    }
    set passwordData(newVal: string | undefined) {
        this.$emit('update:password', newVal);
    }
    get sshKeyData(): string | undefined {
        return this.sshKey;
    }
    set sshKeyData(newVal: string | undefined) {
        this.$emit('update:sshKey', newVal);
    }
    get sshKeyPassphraseData(): string | undefined {
        return this.sshKeyPassphrase;
    }
    set sshKeyPassphraseData(newVal: string | undefined) {
        this.$emit('update:sshKeyPassphrase', newVal);
    }
    get isDisplayPassword(): boolean {
        return !this.isRandomizeTypeComprehensive && (this.isPassword || this.isKey) && this.isEditable;
    }
    get isDisplayKey(): boolean {
        return !this.isRandomizeTypeComprehensive && this.isKey && this.isEditable;
    }

    /**
     * methods
     */
    reset(): void {
        [
            (this as any).$refs['password'],
            (this as any).$refs['secret-key'],
            (this as any).$refs['secret-key-passphrase'],
        ].forEach(e => {
            if (e) {
                e.reset();
            }
        });
        this.isShowPassword = false;
        this.isShowPassphrase = false;
    }
}
</script>
